import { useCatalog } from "@/lib/catalog/context.tsx";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { firstUnwatched } from "@/lib/road/progress.ts";
import { scrollToEntry } from "@/lib/road/scrollToEntry.ts";
import { useProgressStore } from "@/state/progress.ts";
import { type ThemeSource, useSettingsStore } from "@/state/settings.ts";
import { AccountDialog } from "./account/AccountDialog.tsx";
import { BackupDialog } from "./backup/BackupDialog.tsx";
import styles from "./Header.module.css";
import { ExportDialog } from "./letterboxd/ExportDialog.tsx";
import { ImportDialog } from "./letterboxd/ImportDialog.tsx";

const THEME_SOURCE_LABELS: Record<ThemeSource, string> = {
  scroll: "follows scroll",
  progress: "follows progress",
  off: "off",
};

export function Header() {
  const catalog = useCatalog();
  const watchedCount = useProgressStore(
    (s) => catalog.entries.filter((e) => s.watched[e.id]).length,
  );
  const themeSource = useSettingsStore((s) => s.themeSource);
  const setThemeSource = useSettingsStore((s) => s.setThemeSource);
  const showRating = useSettingsStore((s) => s.showRating);
  const setShowRating = useSettingsStore((s) => s.setShowRating);
  const features = catalog.project.features;

  // Against the filtered road, not the catalog: "next" has to mean the next
  // stop actually on screen, or the jump lands on nothing.
  const { entries: roadEntries } = useFilteredRoad();
  const watchedMap = useProgressStore((s) => s.watched);
  const next = firstUnwatched(roadEntries, watchedMap);

  const targetDate = new Date(`${catalog.target.releaseDate}T00:00:00`);
  const dateLabel = targetDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <p className={styles.kicker}>Converging on</p>
        <h1 className={styles.title}>{catalog.target.title}</h1>
        <p className={styles.sub}>
          In theaters {dateLabel}. {catalog.totals.entryCount} {catalog.project.vocabulary.stops},{" "}
          {Math.round(catalog.totals.runtimeMinutes / 60)} hours end to end. You've made{" "}
          <strong>
            {watchedCount} of {catalog.totals.entryCount}
          </strong>
          .
        </p>
        <div className={styles.actions}>
          {/* Absent once everything visible is watched — a button that jumps
              nowhere is worse than no button. */}
          {next && (
            <button
              type="button"
              className={styles.nextUp}
              onClick={() => scrollToEntry(next.id)}
              title={next.title}
            >
              Next up: {next.title}
            </button>
          )}
          <label className={styles.themePicker}>
            Theme
            <select
              value={themeSource}
              onChange={(event) => setThemeSource(event.target.value as ThemeSource)}
            >
              {(Object.keys(THEME_SOURCE_LABELS) as ThemeSource[]).map((source) => (
                <option key={source} value={source}>
                  {THEME_SOURCE_LABELS[source]}
                </option>
              ))}
            </select>
          </label>
          {/* A display preference, so it sits with the theme picker rather
           * than in the filter panel — it changes how the road is presented,
           * not which stops are on it. */}
          {features.rating && (
            <label className={styles.ratingToggle}>
              <input
                type="checkbox"
                checked={showRating}
                onChange={(event) => setShowRating(event.target.checked)}
              />
              Show {catalog.project.vocabulary.rating}
            </label>
          )}
          {/*
           * Gated by the project's feature flags, not hidden by CSS: a
           * deployment that does not offer accounts should not ship the
           * dialog, the sync client, or a sign-in form that posts nowhere.
           */}
          {features.letterboxd && <ImportDialog />}
          {features.letterboxd && <ExportDialog />}
          {features.backup && <BackupDialog />}
          {features.accounts && <AccountDialog />}
        </div>
      </div>
    </header>
  );
}
